import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, FileText, Users as UsersIcon, Settings, LayoutDashboard } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import AdminDashboardPanel from "@/components/admin/AdminDashboardPanel";
import UploadPanel from "@/components/admin/UploadPanel";
import PathwaysPanel from "@/components/admin/PathwaysPanel";
import GlossaryPanel from "@/components/admin/GlossaryPanel";
import TrashPanel from "@/components/admin/TrashPanel";
import AuthorsPanel from "@/components/admin/AuthorsPanel";
import UsersPanel from "@/components/admin/UsersPanel";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import PageSectionsPanel from "@/components/admin/PageSectionsPanel";
import PathwayIntrosPanel from "@/components/admin/PathwayIntrosPanel";
import SearchSettingsPanel from "@/components/admin/SearchSettingsPanel";
import CommentsPanel from "@/components/admin/CommentsPanel";
import FeedbackPanel from "@/components/admin/FeedbackPanel";
import FooterPanel from "@/components/admin/FooterPanel";
import BlogDisplayPanel from "@/components/admin/BlogDisplayPanel";
import VisualEditorPanel from "@/components/admin/VisualEditorPanel";
import BlogEditorUISettingsPanel from "@/components/admin/BlogEditorUISettingsPanel";
import KeyboardShortcutsPanel from "@/components/admin/KeyboardShortcutsPanel";
import PageVisibilityPanel from "@/components/admin/PageVisibilityPanel";

export default function Admin() {
  const { user, loading } = useAuth();

  const [blogs, setBlogs] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [users, setUsers] = useState([]);
  const [commentCounts, setCommentCounts] = useState({ unreviewed: 0 });
  const [feedbackCounts, setFeedbackCounts] = useState({ unreviewed: 0 });
  // The active tab is derived from `?tab=X` so external navigators (search
  // page, command palette, deep links) can jump straight to any admin
  // panel. Defaults to "upload" when no param is present.
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("tab") || "dashboard";
  const setActive = (val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val && val !== "dashboard") next.set("tab", val);
      else next.delete("tab");
      return next;
    }, { replace: true });
  };

  const refresh = () => {
    api.get("/blogs").then(r => setBlogs(r.data));
    api.get("/pathways?include_hidden=true").then(r => setPathways(r.data));
    api.get("/glossary?include_hidden=true").then(r => setGlossary(r.data));
    api.get("/admin/comments/counts").then(r => setCommentCounts(r.data)).catch(() => {});
    api.get("/admin/feedback/counts").then(r => setFeedbackCounts(r.data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const loadUsers = () => api.get("/users").then(r => setUsers(r.data));

  // Tab metadata grouped by section. Each item can carry an optional `badge` count.
  const groups = [
    {
      key: "content", label: "Content", Icon: FileText,
      testid: "admin-group-content",
      items: [
        { value: "upload", label: "Upload blog", testid: ADMIN.tabUpload },
        { value: "pathways", label: "Pathways", testid: ADMIN.tabPathways },
        { value: "glossary", label: "Glossary", testid: ADMIN.tabGlossary },
        { value: "trash", label: "Trash", testid: "admin-tab-trash" },
      ],
    },
    {
      key: "people", label: "People & data", Icon: UsersIcon,
      testid: "admin-group-people",
      items: [
        { value: "authors", label: "Authors", testid: "admin-tab-authors" },
        { value: "comments", label: "Comments", testid: "admin-tab-comments", badge: commentCounts.unreviewed },
        { value: "feedback", label: "Feedback", testid: "admin-tab-feedback", badge: feedbackCounts.unreviewed },
        { value: "users", label: "Users", testid: ADMIN.tabUsers, onClick: loadUsers },
        { value: "analytics", label: "Analytics", testid: ADMIN.tabAnalytics },
      ],
    },
    {
      key: "site", label: "Site & Editors", Icon: Settings,
      testid: "admin-group-site",
      items: [
        { value: "search", label: "Search settings", testid: "admin-tab-search" },
        { value: "page-visibility", label: "Page visibility", testid: "admin-tab-page-visibility" },
        { value: "home", label: "Home page visual editor", testid: "admin-tab-home" },
        { value: "bookmarks-intro", label: "Bookmarks visual editor", testid: "admin-tab-bookmarks-intro" },
        { value: "glossary-intro", label: "Glossary visual editor", testid: "admin-tab-glossary-intro" },
        { value: "tools-and-metrics", label: "Tools and Metrics visual editor", testid: "admin-tab-tools-and-metrics" },
        { value: "pathway-intros", label: "Pathways visual editor", testid: "admin-tab-pathway-intros" },
        { value: "blog-display", label: "Blog display settings", testid: "admin-tab-blog-display" },
        { value: "visual-editor", label: "Global section editor", testid: "admin-tab-visual-editor" },
        { value: "shortcuts", label: "Keyboard shortcuts", testid: "admin-tab-shortcuts" },
        { value: "editor-ui", label: "Blog editor workspace", testid: "admin-tab-editor-ui" },
        { value: "footer", label: "Footer & Socials", testid: "admin-tab-footer" },
      ],
    },
  ];

  const pickTab = (value, onClick) => {
    setActive(value);
    if (onClick) onClick();
  };

  return (
    <div data-testid={ADMIN.dashboard} className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <div className="text-xs uppercase tracking-[0.24em] text-sky-700 mb-3">Admin</div>
      <h1 className="font-heading text-4xl font-semibold text-slate-900">Content dashboard</h1>
      <p className="mt-3 text-slate-600">Upload Word document blogs, curate pathways, manage the glossary and users.</p>

      <Tabs value={active} onValueChange={setActive} className="mt-8">
        {/* Header-style nav — three primary headers with dropdown menus. */}
        <nav className="border-b border-slate-200" data-testid="admin-nav">
          <div className="flex flex-wrap items-stretch gap-x-8 gap-y-2 -mb-px">
            {/* Standalone Home / Dashboard pill — appears before the group
               dropdowns so admins land here by default. */}
            <button
              data-testid="admin-tab-dashboard"
              onClick={() => setActive("dashboard")}
              className={`inline-flex items-center gap-2 pb-3 pt-1 text-sm border-b-2 transition-colors outline-none
                ${active === "dashboard"
                  ? "border-sky-700 text-sky-900"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"}`}
            >
              <LayoutDashboard className={`w-4 h-4 ${active === "dashboard" ? "opacity-100" : "opacity-60"}`}/>
              <span className="font-heading font-semibold tracking-tight">Home</span>
            </button>
            {groups.map(({ key, label, Icon, testid, items }) => {
              const activeItem = items.find(i => i.value === active);
              const groupHasActive = !!activeItem;
              const groupBadgeTotal = items.reduce((n, i) => n + (i.badge || 0), 0);
              return (
                <DropdownMenu key={key}>
                  <DropdownMenuTrigger data-testid={testid}
                    className={`inline-flex items-center gap-2 pb-3 pt-1 text-sm border-b-2 transition-colors outline-none
                      ${groupHasActive
                        ? "border-sky-700 text-sky-900"
                        : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"}`}>
                    <Icon className={`w-4 h-4 ${groupHasActive ? "opacity-100" : "opacity-60"}`}/>
                    <span className="font-heading font-semibold tracking-tight">{label}</span>
                    {groupBadgeTotal > 0 && (
                      <span data-testid={`${testid}-badge`}
                            className="text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white rounded-full px-1.5 py-0.5">
                        {groupBadgeTotal}
                      </span>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 opacity-60"/>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-normal">
                      {label}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator/>
                    {items.map(it => (
                      <DropdownMenuItem key={it.value} data-testid={it.testid}
                        onSelect={() => pickTab(it.value, it.onClick)}
                        className={`justify-between ${active === it.value ? "bg-sky-50 text-sky-900" : ""}`}>
                        <span>{it.label}</span>
                        {(it.badge || 0) > 0 && (
                          <span data-testid={`${it.testid}-badge`}
                                className="text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white rounded-full px-1.5 py-0.5">
                            {it.badge}
                          </span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </div>
          {/* Sub-label showing which item is currently open within the active group */}
          {(() => {
            const g = groups.find(g => g.items.some(i => i.value === active));
            const it = g?.items.find(i => i.value === active);
            if (!it) return null;
            return (
              <div className="mt-3 text-xs text-slate-500">
                <span className="uppercase tracking-[0.2em] text-slate-400">{g.label}</span>
                <span className="mx-2 text-slate-300">/</span>
                <span className="font-medium text-slate-700">{it.label}</span>
              </div>
            );
          })()}
        </nav>

        <TabsContent value="dashboard">
          <AdminDashboardPanel
            blogs={blogs}
            pathways={pathways}
            glossary={glossary}
            commentCounts={commentCounts}
            feedbackCounts={feedbackCounts}
            onNavigate={(tab) => setActive(tab)}
          />
        </TabsContent>
        <TabsContent value="upload"><UploadPanel blogs={blogs} refresh={refresh}/></TabsContent>
        <TabsContent value="pathways"><PathwaysPanel blogs={blogs} pathways={pathways} refresh={refresh}/></TabsContent>
        <TabsContent value="glossary"><GlossaryPanel glossary={glossary} refresh={refresh}/></TabsContent>
        <TabsContent value="trash"><TrashPanel refresh={refresh}/></TabsContent>
        <TabsContent value="authors"><AuthorsPanel/></TabsContent>
        <TabsContent value="comments"><CommentsPanel/></TabsContent>
        <TabsContent value="feedback">
          <FeedbackPanel onCountsChange={() =>
            api.get("/admin/feedback/counts").then(r => setFeedbackCounts(r.data)).catch(() => {})}/>
        </TabsContent>
        <TabsContent value="users"><UsersPanel me={user} users={users} reload={loadUsers}/></TabsContent>
        <TabsContent value="analytics"><AnalyticsPanel/></TabsContent>
        <TabsContent value="home"><PageSectionsPanel pageKey="home"/></TabsContent>
        <TabsContent value="glossary-intro"><PageSectionsPanel pageKey="glossary_intro"/></TabsContent>
        <TabsContent value="bookmarks-intro"><PageSectionsPanel pageKey="bookmarks_intro"/></TabsContent>
        <TabsContent value="tools-and-metrics"><PageSectionsPanel pageKey="tools_and_metrics"/></TabsContent>
        <TabsContent value="page-visibility"><PageVisibilityPanel/></TabsContent>
        <TabsContent value="pathway-intros"><PathwayIntrosPanel/></TabsContent>
        <TabsContent value="blog-display"><BlogDisplayPanel/></TabsContent>
        <TabsContent value="visual-editor"><VisualEditorPanel/></TabsContent>
        <TabsContent value="editor-ui"><BlogEditorUISettingsPanel/></TabsContent>
        <TabsContent value="shortcuts"><KeyboardShortcutsPanel/></TabsContent>
        <TabsContent value="footer"><FooterPanel/></TabsContent>
        <TabsContent value="search"><SearchSettingsPanel/></TabsContent>
      </Tabs>
    </div>
  );
}
